import React, { useState, useEffect, useCallback, useRef } from 'react';
import BilingualReaderV2 from './components/BilingualReaderV2';
import ReaderStatus from './components/ReaderStatus';
import { useUser } from './contexts/UserContext';
import { fetchWithRetry } from './utils/fetchWithRetry';
import { ApiError, fetchApi } from './utils/api';
import {
  getLocalProgress,
  mergeProgress,
  computeReadingProgress,
  PROGRESS_KEY,
  type ReadingProgress,
  type CloudProgress,
} from './utils/reading-progress';
import { useI18n } from './i18n';
import './App.css';

interface Chapter {
  id: number;
  chapter_number: number;
  title: string;
  original_title: string;
  order_index: number;
  text_length?: number | null;
}

interface Translation {
  xpath: string;
  original_text: string;
  original_html?: string;
  translated_text: string;
}

interface ChapterContent {
  uuid: string;
  title: string;
  originalTitle: string;
  author: string;
  styles: string;
  currentChapter: number;
  chapterInfo: {
    number: number;
    title: string;
    originalTitle: string;
  };
  rawHtml: string;
  translations: Translation[];
}

interface AppV2Props {
  bookUuid: string;
  onBackToShelf: () => void;
}

// The API answers 404 when a book doesn't exist or this user can't open it.
class BookNotFoundError extends Error {}

// Fetch cloud progress and merge with local — cloud wins if newer
const fetchAndMergeProgress = async (
  uuid: string
): Promise<ReadingProgress> => {
  const local = getLocalProgress(uuid);

  try {
    const response = await fetchApi(`/api/book/${uuid}/progress`);
    const data = (await response.json()) as { progress: CloudProgress | null };
    const { merged, source } = mergeProgress(local, data.progress);

    // Update localStorage if cloud won
    if (source === 'cloud') {
      localStorage.setItem(PROGRESS_KEY(uuid), JSON.stringify(merged));
    }

    return merged;
  } catch {
    return local;
  }
};

function AppV2({ bookUuid, onBackToShelf }: AppV2Props) {
  const { t } = useI18n();
  const [chapterContent, setChapterContent] = useState<ChapterContent | null>(
    null
  );
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  // Where a failed chapter load was going, so Try again repeats that load.
  const failedLoadRef = useRef<{ chapter: number; xpath?: string } | null>(
    null
  );

  // Initialize from local progress first, then merge with cloud
  const initialProgress = useRef(getLocalProgress(bookUuid));
  const [currentChapter, setCurrentChapter] = useState(
    initialProgress.current.chapter
  );
  const [targetXpath, setTargetXpath] = useState<string | undefined>(
    initialProgress.current.xpath
  );
  // Undefined until cloud merge resolves — reader won't mount until then,
  // so its initial state always reflects the most recent known toggle.
  const [initialShowOriginal, setInitialShowOriginal] = useState<
    boolean | undefined
  >(initialProgress.current.showOriginal);

  // Track current visible xpath and intra-chapter fraction for saving
  const currentXpathRef = useRef<string | undefined>(undefined);
  // Positions to return to after following internal links (footnotes etc.)
  const [returnStack, setReturnStack] = useState<
    { chapter: number; xpath?: string }[]
  >([]);
  const chapterFractionRef = useRef<number>(0);

  // Debounce timer for progress API updates
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);

  // In-memory cache of fetched chapter content, keyed by chapter number.
  // Lets prev/next navigation hit memory instead of the network so the
  // page-turn animation runs without a fetch stall.
  const chapterCacheRef = useRef<Map<number, ChapterContent>>(new Map());
  // True once any chapter rendered successfully this session — gates the
  // unload/hide progress beacon so broken sessions never overwrite cloud state
  const hasLoadedChapterRef = useRef(false);

  // Track book completion status
  const [isCompleted, setIsCompleted] = useState(false);

  // Share state
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [bookOwnerId, setBookOwnerId] = useState<number | null>(null);
  const [languagePair, setLanguagePair] = useState<string | undefined>(
    undefined
  );
  const { user } = useUser();

  // Calculate reading progress percentage — chapters weighted by text length,
  // plus the intra-chapter fraction through the current chapter
  const calculateProgress = useCallback(
    (fraction?: number) => {
      const f = fraction ?? chapterFractionRef.current;
      return computeReadingProgress(chapters, currentChapter, f);
    },
    [currentChapter, chapters]
  );

  // Mark book as complete/incomplete and update progress
  const handleMarkComplete = useCallback(
    async (completed: boolean) => {
      try {
        const progressPercent = calculateProgress();
        const response = await fetch(`/api/book/${bookUuid}/mark-complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            isCompleted: completed,
            readingProgress: completed ? 100 : progressPercent,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setIsCompleted(completed);
          console.log('Book marked complete:', data);
        } else {
          const errData = await response.text();
          throw new Error(`HTTP ${response.status}: ${errData}`);
        }
      } catch (err) {
        console.error('Error marking book complete:', err);
        // BilingualReaderV2 shows this message under the switch.
        throw new Error(t.book.markReadFailed);
      }
    },
    [bookUuid, calculateProgress, t]
  );

  // Save progress to localStorage only (URL stays clean as /book/{uuid})
  const saveProgress = useCallback(
    (chapter: number, xpath?: string) => {
      // When xpath is not provided, preserve the existing saved xpath
      // only if we're on the same chapter (avoids stale xpath from a different chapter)
      if (!xpath) {
        const existing = localStorage.getItem(PROGRESS_KEY(bookUuid));
        if (existing) {
          try {
            const prev = JSON.parse(existing) as ReadingProgress;
            if (prev.chapter === chapter && prev.xpath) {
              xpath = prev.xpath;
            }
          } catch {
            /* ignore */
          }
        }
      }
      const progress: ReadingProgress = {
        chapter,
        xpath,
        timestamp: Date.now(),
      };
      localStorage.setItem(PROGRESS_KEY(bookUuid), JSON.stringify(progress));
    },
    [bookUuid]
  );

  // Called by reader when the Show Translation / Show Original toggle flips.
  // Saves immediately (no debounce) so the user's choice survives a reload.
  const handleShowOriginalChange = useCallback(
    (showOriginal: boolean) => {
      // Mirror into localStorage so offline / unauth users still get persistence.
      try {
        const existing = localStorage.getItem(PROGRESS_KEY(bookUuid));
        const prev: ReadingProgress = existing
          ? JSON.parse(existing)
          : { chapter: currentChapter, timestamp: Date.now() };
        localStorage.setItem(
          PROGRESS_KEY(bookUuid),
          JSON.stringify({ ...prev, showOriginal, timestamp: Date.now() })
        );
      } catch {
        /* ignore */
      }

      const progressPercent = calculateProgress(chapterFractionRef.current);
      fetch(`/api/book/${bookUuid}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          readingProgress: progressPercent,
          chapterNumber: currentChapter,
          paragraphXpath: currentXpathRef.current,
          showOriginal,
        }),
      }).catch((err) => console.error('Error saving showOriginal:', err));
    },
    [bookUuid, currentChapter, calculateProgress]
  );

  // Called by reader when visible element changes
  const handleProgressChange = useCallback(
    (xpath: string, chapterFraction: number) => {
      currentXpathRef.current = xpath;
      chapterFractionRef.current = chapterFraction;
      // Save to localStorage immediately
      saveProgress(currentChapter, xpath);

      // Debounced API update - save chapter + xpath to backend every 5 seconds
      if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
      progressTimerRef.current = setTimeout(() => {
        const progressPercent = calculateProgress(chapterFraction);
        fetch(`/api/book/${bookUuid}/progress`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            readingProgress: progressPercent,
            chapterNumber: currentChapter,
            paragraphXpath: xpath,
          }),
        }).catch((err) =>
          console.error('Error saving progress to backend:', err)
        );
      }, 5000);
    },
    [currentChapter, saveProgress, bookUuid, calculateProgress]
  );

  // Load chapters list
  useEffect(() => {
    const loadChapters = async () => {
      try {
        const response = await fetchWithRetry(
          `/api/v2/book/${bookUuid}/chapters`
        );
        if (response.status === 404) {
          setNotFound(true);
          return;
        }
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setChapters(data as Chapter[]);
      } catch (err) {
        console.error('Error fetching chapters:', err);
      }
    };

    const loadCompletion = async () => {
      try {
        const response = await fetchApi(`/api/book/${bookUuid}/progress`);
        const data = await response.json();
        if (data.progress?.is_completed) {
          setIsCompleted(true);
        }
      } catch (err) {
        console.error('Error fetching completion status:', err);
      }
    };

    const loadShareStatus = async () => {
      try {
        const response = await fetchApi(`/api/book/${bookUuid}/share`);
        const data = (await response.json()) as { token: string | null };
        setShareToken(data.token);
      } catch (err) {
        // Not owner or not logged in — ignore
      }
    };

    // Fetch book metadata to know the owner and language pair
    const loadBookMeta = async () => {
      try {
        const response = await fetchApi('/api/books');
        const books = (await response.json()) as Array<{
          uuid: string;
          user_id: number | null;
          language_pair?: string | null;
        }>;
        const book = books.find((b: any) => b.uuid === bookUuid);
        if (book) {
          setBookOwnerId(book.user_id);
          setLanguagePair(book.language_pair ?? undefined);
        }
      } catch {
        // ignore
      }
    };

    loadChapters();
    loadCompletion();
    loadShareStatus();
    loadBookMeta();
  }, [bookUuid]);

  // Fetch a chapter payload with an in-memory cache so repeat/adjacent
  // navigation is instant. On miss, hits the API and caches the result.
  const fetchChapterData = async (
    chapterNumber: number
  ): Promise<ChapterContent> => {
    const cached = chapterCacheRef.current.get(chapterNumber);
    if (cached) return cached;
    const response = await fetchWithRetry(
      `/api/v2/book/${bookUuid}/chapter/${chapterNumber}`
    );
    if (response.status === 404) throw new BookNotFoundError();
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = (await response.json()) as ChapterContent;
    chapterCacheRef.current.set(chapterNumber, data);
    return data;
  };

  // Background-prefetch a chapter with no loading UI, ignoring failures.
  const prefetchChapter = (chapterNumber: number) => {
    if (chapterNumber < 1) return;
    if (chapters.length > 0 && chapterNumber > chapters.length) return;
    if (chapterCacheRef.current.has(chapterNumber)) return;
    const run = () => {
      fetchChapterData(chapterNumber).catch(() => {});
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void) => void;
    };
    if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(run);
    else setTimeout(run, 200);
  };

  // Load chapter content
  const loadChapter = async (chapterNumber: number, scrollToXpath?: string) => {
    // Cancel any pending progress save from the previous chapter
    // to prevent stale chapter data from overwriting the new chapter
    if (progressTimerRef.current) {
      clearTimeout(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    const cacheHit = chapterCacheRef.current.has(chapterNumber);
    // Skip the "Loading chapter..." flash when we already have the content —
    // this is what makes the page-turn animation feel instant.
    if (!cacheHit) setLoading(true);
    try {
      const data = await fetchChapterData(chapterNumber);
      // The server clamps stale chapter numbers (e.g. a cached position from
      // before a chapter-structure repair) to the nearest valid chapter and
      // reports the real number — adopt it so state and saved progress
      // re-sync instead of persisting the dead number.
      const actualChapter = data.chapterInfo?.number ?? chapterNumber;
      if (actualChapter !== chapterNumber) {
        chapterCacheRef.current.set(actualChapter, data);
      }
      setChapterContent(data);
      setCurrentChapter(actualChapter);
      hasLoadedChapterRef.current = true;
      chapterNumber = actualChapter;
      // Warm the cache for adjacent chapters so the next click is also instant.
      prefetchChapter(chapterNumber - 1);
      prefetchChapter(chapterNumber + 1);

      // Auto-update reading progress in database (fire-and-forget)
      // Use PUT progress endpoint to avoid resetting completion status
      // When entering a chapter, fraction is 0 (top of chapter)
      chapterFractionRef.current = 0;
      if (chapters.length > 0) {
        const progressPercent = computeReadingProgress(
          chapters,
          chapterNumber,
          0
        );
        fetch(`/api/book/${bookUuid}/progress`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            readingProgress: progressPercent,
            chapterNumber: chapterNumber,
            paragraphXpath: scrollToXpath || null,
          }),
        }).catch((err) => console.error('Error updating progress:', err));
      }

      // Set target xpath for scroll (or clear it for new chapter)
      setTargetXpath(scrollToXpath);

      // Only save progress with xpath when we actually have one;
      // don't overwrite good progress with empty/undefined xpath
      if (scrollToXpath) {
        saveProgress(chapterNumber, scrollToXpath);
      } else {
        // Save chapter number only, preserving any existing xpath via
        // a fresh save without xpath (the reader observer will update it soon)
        saveProgress(chapterNumber);
      }

      // If no target xpath, scroll to top
      if (!scrollToXpath) {
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
      }
    } catch (err) {
      if (err instanceof BookNotFoundError) {
        setNotFound(true);
      } else {
        failedLoadRef.current = {
          chapter: chapterNumber,
          xpath: scrollToXpath,
        };
        setError(
          err instanceof ApiError || err instanceof Error
            ? err.message
            : t.book.loadChapterFailed
        );
        console.error('Error fetching chapter:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial chapter load — merge cloud progress, then load
  useEffect(() => {
    const init = async () => {
      const merged = await fetchAndMergeProgress(bookUuid);
      initialProgress.current = merged;

      // If cloud had a different chapter/xpath, update state before loading
      if (merged.chapter !== currentChapter || merged.xpath !== targetXpath) {
        setCurrentChapter(merged.chapter);
        setTargetXpath(merged.xpath);
      }
      setInitialShowOriginal(merged.showOriginal);

      await loadChapter(merged.chapter, merged.xpath);
    };
    init();
  }, [bookUuid]);

  // Flush current reading position to localStorage AND backend on page exit or tab hide
  useEffect(() => {
    const flushProgress = () => {
      // A session that never successfully displayed a chapter has nothing
      // trustworthy to save — beaconing here would overwrite good cloud
      // progress with a stale/broken position (seen after a chapter
      // renumbering: the error page's unload beacon kept restoring the
      // dead chapter number).
      if (!hasLoadedChapterRef.current) return;
      if (currentXpathRef.current) {
        saveProgress(currentChapter, currentXpathRef.current);

        // Also flush to backend using sendBeacon for reliability
        const progressPercent = computeReadingProgress(
          chapters,
          currentChapter,
          chapterFractionRef.current
        );
        const payload = JSON.stringify({
          readingProgress: progressPercent,
          chapterNumber: currentChapter,
          paragraphXpath: currentXpathRef.current,
        });
        // sendBeacon is reliable even during page unload (sends POST)
        // Backend accepts both PUT and POST for progress updates
        navigator.sendBeacon(
          `/api/book/${bookUuid}/progress`,
          new Blob([payload], { type: 'application/json' })
        );
      }
    };

    const handleBeforeUnload = () => flushProgress();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushProgress();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
      }
    };
  }, [currentChapter, saveProgress, bookUuid, chapters]);

  const isOwner = !!(user && bookOwnerId && user.id === bookOwnerId);

  const handleShare = useCallback(async () => {
    const response = await fetch(`/api/book/${bookUuid}/share`, {
      method: 'POST',
    });
    if (response.ok) {
      const data = (await response.json()) as { token: string; url: string };
      setShareToken(data.token);
      // Auto-copy to clipboard
      try {
        await navigator.clipboard.writeText(data.url);
      } catch {
        /* clipboard may fail */
      }
    } else {
      throw new Error(t.book.createShareFailed);
    }
  }, [bookUuid]);

  const handleRevokeShare = useCallback(async () => {
    const response = await fetch(`/api/book/${bookUuid}/share`, {
      method: 'DELETE',
    });
    if (response.ok) {
      setShareToken(null);
    } else {
      throw new Error(t.book.revokeShareFailed);
    }
  }, [bookUuid]);

  // Wrapper for manual chapter navigation (no xpath) - must be before early returns.
  // Returns the Promise so the reader can await it inside document.startViewTransition().
  // Manual navigation invalidates any pending "return to reading" position.
  const handleLoadChapter = useCallback(
    (chapterNumber: number) => {
      setReturnStack([]);
      return loadChapter(chapterNumber);
    },
    [bookUuid]
  );

  // Follow an internal link (footnote jump / cross-reference) — remembers
  // where the reader was so the floating chip can take them back.
  const handleNavigateInternal = useCallback(
    (chapterNumber: number, xpath: string) => {
      setReturnStack((stack) =>
        [
          ...stack,
          { chapter: currentChapter, xpath: currentXpathRef.current },
        ].slice(-5)
      );
      loadChapter(chapterNumber, xpath);
    },
    [bookUuid, currentChapter]
  );

  const handleReturnToReading = useCallback(() => {
    setReturnStack((stack) => {
      const top = stack[stack.length - 1];
      if (top) {
        loadChapter(top.chapter, top.xpath);
      }
      return stack.slice(0, -1);
    });
  }, [bookUuid]);

  if (notFound) {
    return (
      <ReaderStatus
        title={t.book.notFoundTitle}
        actions={
          <button
            className="reader-status-btn reader-status-btn-primary"
            onClick={onBackToShelf}
          >
            {t.reader.backToShelf}
          </button>
        }
      >
        {t.book.notFoundMessage}
      </ReaderStatus>
    );
  }

  if (error) {
    return (
      <ReaderStatus
        title={t.book.loadChapterFailed}
        detail={error !== t.book.loadChapterFailed ? error : undefined}
        actions={
          <>
            <button
              className="reader-status-btn reader-status-btn-primary"
              onClick={() => {
                const target = failedLoadRef.current ?? {
                  chapter: currentChapter,
                };
                setError(null);
                loadChapter(target.chapter, target.xpath);
              }}
            >
              {t.errors.tryAgain}
            </button>
            <button className="reader-status-btn" onClick={onBackToShelf}>
              {t.reader.backToShelf}
            </button>
          </>
        }
      >
        {t.book.loadFailedMessage}
      </ReaderStatus>
    );
  }

  if (loading && !chapterContent) {
    return <ReaderStatus>{t.book.loadingContent}</ReaderStatus>;
  }

  if (!chapterContent) return null;

  return (
    <div className="App">
      <BilingualReaderV2
        rawHtml={chapterContent.rawHtml}
        translations={chapterContent.translations}
        styles={chapterContent.styles}
        title={chapterContent.title}
        author={chapterContent.author}
        currentChapter={currentChapter}
        totalChapters={chapters.length}
        chapters={chapters}
        onLoadChapter={handleLoadChapter}
        isLoading={loading}
        bookUuid={bookUuid}
        languagePair={languagePair}
        onBackToShelf={onBackToShelf}
        // Reading status is per account; signed out there is nothing to save.
        onMarkComplete={user ? handleMarkComplete : undefined}
        isCompleted={isCompleted}
        isOwner={isOwner}
        shareToken={shareToken}
        onShare={handleShare}
        onRevokeShare={handleRevokeShare}
        initialXpath={targetXpath}
        onProgressChange={handleProgressChange}
        initialShowOriginal={initialShowOriginal ?? true}
        onShowOriginalChange={handleShowOriginalChange}
        onNavigateInternal={handleNavigateInternal}
        fetchChapter={fetchChapterData}
      />
      {returnStack.length > 0 && (
        <button className="return-chip" onClick={handleReturnToReading}>
          {t.reader.returnToReading}
        </button>
      )}
    </div>
  );
}

export default AppV2;
